import { idbGet, idbPut } from '@/utils/posOffline/db';

const DEVICE_KEY = 'local';

export function generateUuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function normalizeEnrollments(row) {
    const enrollments = { ...((row && row.enrollments) || {}) };
    // Migrar formato legado (un solo negocio en campos planos).
    if (row && row.usuario_negocio && !enrollments[row.usuario_negocio]) {
        enrollments[row.usuario_negocio] = {
            codigo: row.codigo || null,
            nombre: row.nombre || null,
            certificate: row.certificate || null,
            cert_vence_at: row.cert_vence_at || null,
            folio_seq: Number(row.folio_seq) || 0,
        };
    }
    return enrollments;
}

function mergeDeviceView(row, negocio) {
    if (!row) return null;
    const enrollments = normalizeEnrollments(row);
    // Si piden un negocio concreto, NO caer al enrolamiento de otra cuenta.
    if (negocio) {
        const slot = enrollments[negocio] || null;
        return {
            ...row,
            enrollments,
            device_uuid: row.device_uuid,
            usuario_negocio: negocio,
            codigo: (slot && slot.codigo) || null,
            nombre: (slot && slot.nombre) || null,
            certificate: (slot && slot.certificate) || null,
            cert_vence_at: (slot && slot.cert_vence_at) || null,
            folio_seq: Number((slot && slot.folio_seq) || 0) || 0,
            blocked: !!row.blocked,
            block_reason: row.block_reason || null,
        };
    }
    const activeKey = row.usuario_negocio || null;
    const slot = (activeKey && enrollments[activeKey]) || null;
    return {
        ...row,
        enrollments,
        device_uuid: row.device_uuid,
        usuario_negocio: activeKey,
        codigo: (slot && slot.codigo) || row.codigo || null,
        nombre: (slot && slot.nombre) || row.nombre || null,
        certificate: (slot && slot.certificate) || row.certificate || null,
        cert_vence_at: (slot && slot.cert_vence_at) || row.cert_vence_at || null,
        folio_seq: Number((slot && slot.folio_seq) ?? row.folio_seq) || 0,
        blocked: !!row.blocked,
        block_reason: row.block_reason || null,
    };
}

export async function getOrCreateDeviceUuid() {
    const row = await idbGet('device', DEVICE_KEY);
    if (row && row.device_uuid) return row.device_uuid;
    const device_uuid = generateUuid();
    await idbPut('device', {
        id: DEVICE_KEY,
        device_uuid,
        codigo: null,
        blocked: false,
        enrollments: {},
    });
    return device_uuid;
}

/** Registro del dispositivo; si pasás negocio, usa el enrolamiento de esa cuenta. */
export async function getDeviceRecord(negocio = null) {
    const row = await idbGet('device', DEVICE_KEY);
    return mergeDeviceView(row, negocio);
}

export async function saveDeviceEnrollment(data) {
    const prev = (await idbGet('device', DEVICE_KEY)) || { id: DEVICE_KEY };
    const negocio = data.usuario_negocio != null ? String(data.usuario_negocio) : null;
    const enrollments = normalizeEnrollments(prev);
    if (negocio) {
        const prevSlot = enrollments[negocio] || {};
        enrollments[negocio] = {
            ...prevSlot,
            codigo: data.codigo != null ? data.codigo : prevSlot.codigo || null,
            nombre: data.nombre != null ? data.nombre : prevSlot.nombre || null,
            certificate:
                data.certificate !== undefined
                    ? data.certificate
                    : prevSlot.certificate || null,
            cert_vence_at:
                data.cert_vence_at !== undefined
                    ? data.cert_vence_at
                    : prevSlot.cert_vence_at || null,
            folio_seq: Number(prevSlot.folio_seq) || Number(prev.folio_seq) || 0,
        };
    }

    const active = negocio && enrollments[negocio] ? enrollments[negocio] : null;
    await idbPut('device', {
        ...prev,
        id: DEVICE_KEY,
        device_uuid: data.device_uuid || prev.device_uuid,
        enrollments,
        usuario_negocio: negocio || prev.usuario_negocio || null,
        codigo: (active && active.codigo) || data.codigo || prev.codigo || null,
        nombre: (active && active.nombre) || data.nombre || prev.nombre || null,
        certificate:
            active && active.certificate !== undefined
                ? active.certificate
                : data.certificate !== undefined
                  ? data.certificate
                  : prev.certificate || null,
        cert_vence_at:
            (active && active.cert_vence_at) ||
            data.cert_vence_at ||
            prev.cert_vence_at ||
            null,
        blocked: false,
    });
}

export async function setDeviceBlocked(blocked, reason) {
    const prev = (await idbGet('device', DEVICE_KEY)) || { id: DEVICE_KEY };
    await idbPut('device', {
        ...prev,
        id: DEVICE_KEY,
        blocked: !!blocked,
        block_reason: reason || null,
    });
}

export async function isDeviceBlocked() {
    const d = await idbGet('device', DEVICE_KEY);
    return !!(d && d.blocked);
}

export async function exportDeviceKeyPair() {
    const record = await idbGet('device', 'crypto');
    return record || null;
}

export async function saveDeviceKeyPair({ privateKey, publicKeySpki, certificate }) {
    const prev = (await idbGet('device', 'crypto')) || { id: 'crypto' };
    await idbPut('device', {
        ...prev,
        id: 'crypto',
        privateKey: privateKey != null ? privateKey : prev.privateKey,
        publicKeySpki: publicKeySpki != null ? publicKeySpki : prev.publicKeySpki,
        certificate: certificate !== undefined ? certificate : prev.certificate,
    });
}

/** Limpia certificado del negocio indicado (sin borrar enrolamientos de otras cuentas). */
export async function clearDeviceCertificate(negocio = null) {
    const row = (await idbGet('device', DEVICE_KEY)) || { id: DEVICE_KEY };
    const enrollments = normalizeEnrollments(row);
    const target = negocio || row.usuario_negocio || null;
    if (target && enrollments[target]) {
        enrollments[target] = {
            ...enrollments[target],
            certificate: null,
            cert_vence_at: null,
        };
    }
    const mirrorCleared = !target || row.usuario_negocio === target;
    await idbPut('device', {
        ...row,
        id: DEVICE_KEY,
        enrollments,
        certificate: mirrorCleared ? null : row.certificate || null,
        cert_vence_at: mirrorCleared ? null : row.cert_vence_at || null,
    });

    // El cert en 'crypto' es espejo del último enrolamiento activo.
    if (mirrorCleared) {
        const cryptoRecord = await exportDeviceKeyPair();
        if (cryptoRecord) {
            await idbPut('device', { ...cryptoRecord, id: 'crypto', certificate: null });
        }
    }
}

/** Actualiza folio_seq del enrolamiento de un negocio (y espejo legado). */
export async function bumpDeviceFolioSeq(negocio, nextSeq, codigo) {
    const prev = (await idbGet('device', DEVICE_KEY)) || { id: DEVICE_KEY };
    const enrollments = normalizeEnrollments(prev);
    const key = negocio ? String(negocio) : prev.usuario_negocio;
    if (key) {
        enrollments[key] = {
            ...(enrollments[key] || {}),
            codigo: codigo || (enrollments[key] && enrollments[key].codigo) || prev.codigo || null,
            folio_seq: nextSeq,
        };
    }
    await idbPut('device', {
        ...prev,
        id: DEVICE_KEY,
        enrollments,
        codigo: codigo || prev.codigo || null,
        folio_seq: nextSeq,
        usuario_negocio: key || prev.usuario_negocio || null,
    });
}
